import { Button, ButtonGroup, LuIcon, radiusCva, shadowCva, Text, ToggleGroup, useProvider } from "@overmap-ai/blocks"
import { cx } from "class-variance-authority"
import type { Polygon } from "geojson"
import { Circle, Diamond, Eclipse, Move, Pentagon, Radius, Scale3d, Square } from "lucide-react"
import { memo, useCallback, useEffect, useState } from "react"
import { useMap } from "react-map-gl/mapbox"

import { ringCva } from "../../cva"
import {
	CircleDrawer,
	DiamondDrawer,
	EllipseDrawer,
	GeometryEditor,
	Layer,
	PolygonDrawer,
	PolygonEditor,
	RectangleDrawer,
	SectorDrawer,
	Source,
} from "../../mapbox/components"
import { BOUNDS_PADDING, DRAW_FILL_LAYER_PAINT, POLYGON_GEOMETRY_TYPE_FILTER } from "../../mapbox/constants"
import { GeometryEditMode, MapSlotLayerId } from "../../mapbox/enums"
import { GEOMETRY_DRAW_MODE_INSTRUCTIONS } from "../../mapbox/mappings"
import { polygonToLngLatBounds } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { BottomBar } from "../BottomBar/BottomBar"

enum PolygonPickerLayerId {
	FILL = "polygon-picker-fill-layer",
}

enum PolygonDrawMode {
	POLYGON = "polygon",
	RECTANGLE = "rectangle",
	CIRCLE = "circle",
	SECTOR = "sector",
	ELLIPSE = "ellipse",
	DIAMOND = "diamond",
}

interface PolygonPickerOverlayProps {
	initialPolygon: Polygon
	onConfirm: (polygon: Polygon) => void
	onCancel: () => void
}

export const PolygonPickerOverlay = memo((props: PolygonPickerOverlayProps) => {
	const { initialPolygon, onConfirm, onCancel } = props

	const { radius } = useProvider()
	const { current: map } = useMap()
	const [polygon, setPolygon] = useState<Polygon>(initialPolygon)
	const [polygonDrawMode, setPolygonDrawMode] = useState<PolygonDrawMode | "">("")
	const [polygonEditMode, setPolygonEditMode] = useState<GeometryEditMode | "">(GeometryEditMode.MOVE)

	useEffect(() => {
		if (!map) return
		map?.fitBounds(polygonToLngLatBounds(initialPolygon), { padding: BOUNDS_PADDING, animate: false })
	}, [initialPolygon, map])

	const handleModeChange = useCallback((mode: PolygonDrawMode | "") => {
		setPolygonDrawMode(mode)
	}, [])

	const handleEditModeChange = useCallback((mode: GeometryEditMode | "") => {
		switch (mode) {
			case GeometryEditMode.MOVE:
				setPolygonEditMode(GeometryEditMode.MOVE)
				break
			case GeometryEditMode.EDIT:
				setPolygonEditMode(GeometryEditMode.EDIT)
				break
		}
	}, [])

	const handlePolygonDraw = useCallback((polygon: Polygon) => {
		setPolygon(polygon)
		setPolygonDrawMode("")
	}, [])

	const handleConfirm = useCallback(() => {
		onConfirm(polygon)
	}, [polygon, onConfirm])

	return (
		<>
			{polygonDrawMode === "" && (
				<>
					<Source type="geojson" data={polygon}>
						<Layer
							id={PolygonPickerLayerId.FILL}
							type="fill"
							paint={DRAW_FILL_LAYER_PAINT}
							filter={POLYGON_GEOMETRY_TYPE_FILTER}
							beforeId={MapSlotLayerId.DRAW}
						/>
					</Source>
					{polygonEditMode === GeometryEditMode.MOVE ? (
						<GeometryEditor
							geometry={polygon}
							// TODO: Generically type GeometryEditor to avoid needing ts-expect-error
							// @ts-expect-error REASON: GeometryEditor respects the passed in geometry type
							onGeometryChange={setPolygon}
						/>
					) : null}
					{polygonEditMode === GeometryEditMode.EDIT ? (
						<PolygonEditor polygon={polygon} onPolygonChange={setPolygon} />
					) : null}
				</>
			)}
			{polygonDrawMode === PolygonDrawMode.POLYGON && <PolygonDrawer onComplete={handlePolygonDraw} />}
			{polygonDrawMode === PolygonDrawMode.RECTANGLE && <RectangleDrawer onComplete={handlePolygonDraw} />}
			{polygonDrawMode === PolygonDrawMode.CIRCLE && <CircleDrawer onComplete={handlePolygonDraw} />}
			{polygonDrawMode === PolygonDrawMode.SECTOR && <SectorDrawer onComplete={handlePolygonDraw} />}
			{polygonDrawMode === PolygonDrawMode.ELLIPSE && <EllipseDrawer onComplete={handlePolygonDraw} />}
			{polygonDrawMode === PolygonDrawMode.DIAMOND && <DiamondDrawer onComplete={handlePolygonDraw} />}

			<div className="pointer-events-auto absolute right-4 top-1/2 flex -translate-y-1/2 flex-col gap-4">
				<ButtonGroup
					className={cx(
						"bg-(--color-background) flex flex-col",
						ringCva({ alpha: false }),
						radiusCva({ radius }),
						shadowCva({ size: "md" }),
					)}
					variant="soft"
					accentColor="base"
				>
					<ToggleGroup.Root
						className="flex flex-col"
						type="single"
						variant="soft"
						value={polygonDrawMode}
						accentColor="base"
						onValueChange={handleModeChange}
					>
						<ToggleGroup.IconItem className="rounded-none" value={PolygonDrawMode.POLYGON}>
							<LuIcon icon={Pentagon} />
						</ToggleGroup.IconItem>
						<ToggleGroup.IconItem className="rounded-none" value={PolygonDrawMode.RECTANGLE}>
							<LuIcon icon={Square} />
						</ToggleGroup.IconItem>
						<ToggleGroup.IconItem className="rounded-none" value={PolygonDrawMode.CIRCLE}>
							<LuIcon icon={Circle} />
						</ToggleGroup.IconItem>
						<ToggleGroup.IconItem className="rounded-none" value={PolygonDrawMode.SECTOR}>
							<LuIcon icon={Radius} />
						</ToggleGroup.IconItem>
						<ToggleGroup.IconItem className="rounded-none" value={PolygonDrawMode.ELLIPSE}>
							<LuIcon icon={Eclipse} />
						</ToggleGroup.IconItem>
						<ToggleGroup.IconItem className="rounded-t-none" value={PolygonDrawMode.DIAMOND}>
							<LuIcon icon={Diamond} />
						</ToggleGroup.IconItem>
					</ToggleGroup.Root>
				</ButtonGroup>

				{polygonDrawMode === "" ? (
					<ButtonGroup
						className={cx(
							"bg-(--color-background) flex flex-col",
							ringCva({ alpha: false }),
							radiusCva({ radius }),
							shadowCva({ size: "md" }),
						)}
						variant="soft"
						accentColor="base"
					>
						<ToggleGroup.Root
							className="flex flex-col"
							type="single"
							variant="soft"
							value={polygonEditMode}
							accentColor="base"
							onValueChange={handleEditModeChange}
						>
							<ToggleGroup.IconItem className="rounded-b-none" value={GeometryEditMode.MOVE}>
								<LuIcon icon={Move} />
							</ToggleGroup.IconItem>
							<ToggleGroup.IconItem className="rounded-t-none" value={GeometryEditMode.EDIT}>
								<LuIcon icon={Scale3d} />
							</ToggleGroup.IconItem>
						</ToggleGroup.Root>
					</ButtonGroup>
				) : null}
			</div>

			<BottomBar className="flex flex-col gap-2">
				<Text size="sm" accentColor="base" weight="medium" align="center">
					{polygonDrawMode
						? GEOMETRY_DRAW_MODE_INSTRUCTIONS[polygonDrawMode]
						: "Select a shape tool to draw geometry."}
				</Text>
				<ButtonGroup className="flex w-full gap-2" size="sm" variant="soft" accentColor="base">
					<Button onClick={onCancel} className="grow">
						<LuIcon icon={modelIconMapping.close} />
						Cancel
					</Button>
					<Button onClick={handleConfirm} className="grow" accentColor="primary" variant="surface">
						<LuIcon icon={modelIconMapping.check} />
						Confirm
					</Button>
				</ButtonGroup>
			</BottomBar>
		</>
	)
})
PolygonPickerOverlay.displayName = "PolygonPickerOverlay"
